import React, { useState } from "react";
import { createPortal } from "react-dom";
import { API } from "@/lib/api";
import { Eye, DownloadSimple, X } from "@phosphor-icons/react";

/**
 * CvActions — icon-only Preview + Download toolbar for CV files.
 *
 * Reads: candidate.resume_r2_key OR candidate.resume_url
 * If neither, renders nothing (empty state handled by CvFreshnessBadge
 * or MyResumeCard elsewhere).
 *
 * Compact mode (list-row): no labels, small icons.
 * Default mode (drawer/detail): buttons with labels.
 */
export default function CvActions({ candidate, compact = false, className = "" }) {
    const [showPreview, setShowPreview] = useState(false);

    const hasCv = !!(candidate?.resume_r2_key || candidate?.resume_url);
    if (!hasCv || !candidate?.id) return null;

    const url = `${API}/storage/cv/${candidate.id}`;
    const stopBubble = (e) => e.stopPropagation();

    const btnBase = compact
        ? "border border-[#E5E5E5] hover:border-cobalt hover:text-cobalt text-[#525252] p-1.5 transition-colors"
        : "border border-[#E5E5E5] hover:border-cobalt hover:text-cobalt text-[#525252] px-3 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors";

    return (
        <>
            <div
                className={`inline-flex items-center gap-1 ${className}`}
                onClick={stopBubble}
                data-testid={`cv-actions-${candidate.id}`}
            >
                <button
                    type="button"
                    title="Preview CV"
                    aria-label={`Preview ${candidate.name || "candidate"}'s CV`}
                    onClick={(e) => { stopBubble(e); setShowPreview(true); }}
                    data-testid={`cv-preview-btn-${candidate.id}`}
                    className={btnBase}
                >
                    <Eye size={compact ? 12 : 11} weight="bold" />
                    {!compact && <span>Preview</span>}
                </button>
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    title="Download CV"
                    aria-label={`Download ${candidate.name || "candidate"}'s CV`}
                    data-testid={`cv-download-btn-${candidate.id}`}
                    onClick={stopBubble}
                    className={btnBase}
                >
                    <DownloadSimple size={compact ? 12 : 11} weight="bold" />
                    {!compact && <span>Download</span>}
                </a>
            </div>

            {showPreview && createPortal(
                <div
                    data-testid={`cv-preview-modal-${candidate.id}`}
                    className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4"
                    onMouseDown={(e) => {
                        // Close only when the mousedown starts on the backdrop itself,
                        // not when a drag/select ends here after starting on child.
                        if (e.target === e.currentTarget) setShowPreview(false);
                    }}
                >
                    <div
                        className="bg-white w-full max-w-4xl h-[85vh] rounded-lg overflow-hidden shadow-2xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0 gap-3">
                            <h3 className="text-sm font-bold text-slate-800 truncate min-w-0">
                                {candidate.name || "Resume"} — CV
                            </h3>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setShowPreview(false); }}
                                data-testid={`cv-preview-close-${candidate.id}`}
                                aria-label="Close CV preview"
                                className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-full p-1.5 shrink-0 inline-flex items-center justify-center transition-colors"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </header>
                        <div className="flex-1 bg-slate-100 p-2 md:p-3 overflow-hidden">
                            <iframe
                                src={url}
                                title={`${candidate.name || "Candidate"} CV`}
                                className="w-full h-full bg-white shadow-md rounded-sm"
                                data-testid={`cv-preview-iframe-${candidate.id}`}
                            />
                        </div>
                        <footer className="px-4 py-2 border-t border-slate-100 flex items-center justify-between shrink-0 text-[10px] text-slate-500">
                            <span>Auth-gated · viewable to your team (admin, recruiter, hiring manager)</span>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                onClick={(e) => e.stopPropagation()}
                                className="text-cobalt hover:underline inline-flex items-center gap-1 font-bold"
                            >
                                <DownloadSimple size={10} weight="bold" />
                                Download
                            </a>
                        </footer>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
